import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { challengeAndVerifyMfa } from '../../lib/queries/auth-security';

interface Props {
  factorId: string;
  onVerified: () => void;
  onCancel: () => void;
}

export default function MFAVerifyModal({ factorId, onVerified, onCancel }: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  function handleChange(index: number, value: string) {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError(null);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent) {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newCode = [...code];
    for (let i = 0; i < pasted.length; i++) {
      newCode[i] = pasted[i];
    }
    setCode(newCode);
    if (pasted.length === 6) {
      handleVerify(pasted);
    } else {
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  }

  async function handleVerify(fullCode?: string) {
    const codeStr = fullCode ?? code.join('');
    if (codeStr.length !== 6) {
      setError(t('mfa.enter_6_digit_code'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await challengeAndVerifyMfa(factorId, codeStr);
      onVerified();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('mfa.verification_failed');
      setError(msg);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-2xl animate-slide-up">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-blue-500/10 border border-blue-500/20 rounded-2xl mb-4">
            <Shield className="w-7 h-7 text-blue-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('mfa.two_factor_auth')}</h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            {t('mfa.enter_code_from_app')}
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-3 mb-5">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
          {code.map((digit, i) => (
            <input
              key={i}
              ref={el => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e.target.value)}
              onKeyDown={e => handleKeyDown(i, e)}
              disabled={loading}
              className="w-11 h-13 text-center text-xl font-mono font-bold text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all disabled:opacity-50"
            />
          ))}
        </div>

        <button
          onClick={() => handleVerify()}
          disabled={loading || code.join('').length !== 6}
          className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-semibold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('mfa.verifying')}
            </>
          ) : (
            t('mfa.verify')
          )}
        </button>

        <button
          onClick={onCancel}
          disabled={loading}
          className="w-full mt-3 text-slate-600 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 text-sm py-2 transition-colors disabled:opacity-50"
        >
          {t('mfa.cancel_sign_out')}
        </button>

        <p className="text-center text-xs text-slate-700 dark:text-slate-600 mt-4">
          {t('mfa.open_authenticator_hint')}
        </p>
      </div>
    </div>
  );
}
